"use client";

import { useState, useEffect } from "react";
import { Search, X, ChevronDown, ChevronUp, ArrowRight } from "lucide-react";
import CarCard from "@/components/CarCard";
import { Car, FuelType, BodyType, TransmissionType, TargetType } from "@/lib/types";
import {
  BRANDS,
  FUEL_OPTIONS,
  BODY_OPTIONS,
  PRICE_MIN,
  PRICE_MAX,
  KM_MIN,
  KM_MAX,
  DURATION_MAX_LIMIT,
  formatPrice,
} from "@/lib/mock-data";

const TRANSMISSION_OPTIONS: { value: TransmissionType; label: string }[] = [
  { value: "automat", label: "Automat" },
  { value: "manual", label: "Manuál" },
];
const TARGET_OPTIONS: { value: TargetType; label: string }[] = [
  { value: "firmy", label: "Firmy" },
  { value: "podnikatele", label: "Podnikatele" },
  { value: "soukrome", label: "Soukromé osoby" },
];

function applyFilters(
  cars: Car[],
  opts: {
    search: string; brand: string; fuels: FuelType[]; bodies: BodyType[];
    transmissions: TransmissionType[]; inStockOnly: boolean; newOnly: boolean;
    flexi: boolean; kmPerYear: number; durationMax: number;
    priceMin: number; priceMax: number; fourByFour: boolean;
    nav: boolean; towBar: boolean; targets: TargetType[];
    warranty: boolean; tyres: boolean; sort: string;
  }
): Car[] {
  const result = cars.filter((car) => {
    if (opts.search) {
      const q = opts.search.toLowerCase();
      if (!`${car.brand} ${car.model} ${car.variant}`.toLowerCase().includes(q)) return false;
    }
    if (opts.brand !== "all" && car.brand !== opts.brand) return false;
    if (opts.fuels.length > 0 && !opts.fuels.includes(car.fuel)) return false;
    if (opts.bodies.length > 0 && !opts.bodies.includes(car.body)) return false;
    if (opts.transmissions.length > 0 && !opts.transmissions.includes(car.transmission)) return false;
    if (opts.inStockOnly && !car.in_stock) return false;
    if (opts.newOnly && !car.is_new) return false;
    if (opts.flexi && !car.flexi) return false;
    if (car.km_per_year < opts.kmPerYear) return false;
    if (car.duration_months > opts.durationMax) return false;
    if (car.price_monthly < opts.priceMin || car.price_monthly > opts.priceMax) return false;
    if (opts.fourByFour && !car.four_by_four) return false;
    if (opts.nav && !car.navigation) return false;
    if (opts.towBar && !car.tow_bar) return false;
    if (opts.targets.length > 0 && !opts.targets.some((t) => car.targets.includes(t))) return false;
    if (opts.warranty && !car.warranty_included) return false;
    if (opts.tyres && !car.tyres_included) return false;
    return true;
  });
  return result.sort((a, b) => {
    if (opts.sort === "price_asc") return a.price_monthly - b.price_monthly;
    if (opts.sort === "price_desc") return b.price_monthly - a.price_monthly;
    return Number(b.id) - Number(a.id);
  });
}

function SectionWrap({
  label, open, onToggle, children,
}: {
  label: string; open: boolean; onToggle: () => void; children: React.ReactNode;
}) {
  return (
    <div className="border-t border-gray-100">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-800"
      >
        {label}
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>
      {open && <div className="pb-3">{children}</div>}
    </div>
  );
}

function Pill({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "px-2.5 py-1 text-xs rounded-md border font-medium transition-colors cursor-pointer",
        active
          ? "bg-[#1a6ef5] border-[#1a6ef5] text-white"
          : "bg-white border-gray-200 text-gray-600 hover:border-[#1a6ef5] hover:text-[#1a6ef5]",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

function Chk({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer py-0.5 select-none w-full">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 cursor-pointer accent-[#1a6ef5] shrink-0"
      />
      <span className="text-sm text-gray-700">{label}</span>
    </label>
  );
}

export default function FilteredCars({
  cars,
  initialFuels = [],
  initialBodies = [],
}: {
  cars: Car[];
  initialFuels?: FuelType[];
  initialBodies?: BodyType[];
}) {
  const [allCars, setAllCars] = useState<Car[]>(cars);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("nabidky_custom") ?? "[]") as Car[];
      if (!saved.length) return;
      const existingIds = new Set(cars.map((c) => c.id));
      const extra = saved.filter((c) => !existingIds.has(c.id));
      if (extra.length) setAllCars([...extra, ...cars]);
    } catch {}
  }, [cars]);

  const [search, setSearch] = useState("");
  const [brand, setBrand] = useState("all");
  const [fuels, setFuels] = useState<FuelType[]>(initialFuels);
  const [bodies, setBodies] = useState<BodyType[]>(initialBodies);
  const [transmissions, setTransmissions] = useState<TransmissionType[]>([]);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [newOnly, setNewOnly] = useState(false);
  const [flexi, setFlexi] = useState(false);
  const [kmPerYear, setKmPerYear] = useState(KM_MIN);
  const [durationMax, setDurationMax] = useState(DURATION_MAX_LIMIT);
  const [priceMin, setPriceMin] = useState(PRICE_MIN);
  const [priceMax, setPriceMax] = useState(PRICE_MAX);
  const [fourByFour, setFourByFour] = useState(false);
  const [nav, setNav] = useState(false);
  const [towBar, setTowBar] = useState(false);
  const [targets, setTargets] = useState<TargetType[]>([]);
  const [warranty, setWarranty] = useState(false);
  const [tyres, setTyres] = useState(false);
  const [sort, setSort] = useState("price_asc");
  const [withVat, setWithVat] = useState(false);

  // Sections
  const [sBasic, setSBasic] = useState(true);
  const [sParams, setSParams] = useState(true);
  const [sPrice, setSPrice] = useState(true);
  const [sFuel, setSFuel] = useState(true);
  const [sTrans, setSTrans] = useState(true);
  const [sFeatures, setSFeatures] = useState(true);
  const [sBody, setSBody] = useState(true);
  const [sTarget, setSTarget] = useState(true);
  const [sServices, setSServices] = useState(true);

  const toggleArr = <T,>(arr: T[], val: T, setter: (a: T[]) => void) => {
    setter(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  };

  const resetAll = () => {
    setSearch(""); setBrand("all"); setFuels([]); setBodies([]); setTransmissions([]);
    setInStockOnly(false); setNewOnly(false); setFlexi(false);
    setKmPerYear(KM_MIN); setDurationMax(DURATION_MAX_LIMIT);
    setPriceMin(PRICE_MIN); setPriceMax(PRICE_MAX);
    setFourByFour(false); setNav(false); setTowBar(false);
    setTargets([]); setWarranty(false); setTyres(false);
  };

  const isDirty = search !== "" || brand !== "all" || fuels.length > 0 || bodies.length > 0 ||
    transmissions.length > 0 || inStockOnly || newOnly || flexi ||
    kmPerYear !== KM_MIN || durationMax !== DURATION_MAX_LIMIT ||
    priceMin !== PRICE_MIN || priceMax !== PRICE_MAX ||
    fourByFour || nav || towBar || targets.length > 0 || warranty || tyres;

  // Compute filtered cars directly — no useMemo, no stale closures
  const filtered = applyFilters(allCars, {
    search, brand, fuels, bodies, transmissions, inStockOnly, newOnly, flexi,
    kmPerYear, durationMax, priceMin, priceMax, fourByFour, nav, towBar,
    targets, warranty, tyres, sort,
  });

  return (
    <div className="flex gap-6 items-start">
      {/* ── Filter sidebar ── */}
      <aside className="hidden lg:flex-none lg:block w-72 shrink-0">
        <div className="bg-white rounded-xl border border-gray-200">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <span className="font-semibold text-sm text-gray-800">
              Filtrovat{" "}
              <span className="text-gray-400 font-normal">({filtered.length})</span>
            </span>
            {isDirty && (
              <button
                type="button"
                onClick={resetAll}
                className="flex items-center gap-1 text-xs text-[#1a6ef5] hover:underline"
              >
                <X className="h-3 w-3" /> Smazat vše
              </button>
            )}
          </div>

          <div className="px-4 pb-2">
            {/* Search */}
            <div className="pt-3 pb-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Hledejte dle názvu vozu"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-8 pr-3 h-9 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1a6ef5]/30 focus:border-[#1a6ef5]"
                />
              </div>
            </div>

            {/* Značka */}
            <SectionWrap label="Základní" open={sBasic} onToggle={() => setSBasic((v) => !v)}>
              <select
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                className="w-full h-9 text-sm border border-gray-200 rounded-md px-2 mb-2 focus:outline-none focus:ring-2 focus:ring-[#1a6ef5]/30 focus:border-[#1a6ef5] bg-white"
              >
                <option value="all">Všechny značky</option>
                {BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
              <Chk label="Zobrazit jen vozy skladem" checked={inStockOnly} onChange={setInStockOnly} />
              <Chk label="Zobrazit pouze nové vozy" checked={newOnly} onChange={setNewOnly} />
              <Chk label="Flexi ukončení (bez závazku)" checked={flexi} onChange={setFlexi} />
            </SectionWrap>

            {/* Parametry nájmu */}
            <SectionWrap label="Parametry nájmu" open={sParams} onToggle={() => setSParams((v) => !v)}>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                    <span>Kolik najedu za rok?</span>
                    <span className="font-semibold text-gray-800">{formatPrice(kmPerYear)} km</span>
                  </div>
                  <input
                    type="range" min={KM_MIN} max={KM_MAX} step={1000} value={kmPerYear}
                    onChange={(e) => setKmPerYear(Number(e.target.value))}
                    className="w-full accent-[#1a6ef5] cursor-pointer"
                  />
                  <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                    <span>{formatPrice(KM_MIN)}</span><span>{formatPrice(KM_MAX)}</span>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                    <span>Maximální doba nájmu</span>
                    <span className="font-semibold text-gray-800">{durationMax} měsíců</span>
                  </div>
                  <input
                    type="range" min={12} max={DURATION_MAX_LIMIT} step={6} value={durationMax}
                    onChange={(e) => setDurationMax(Number(e.target.value))}
                    className="w-full accent-[#1a6ef5] cursor-pointer"
                  />
                  <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                    <span>12</span><span>{DURATION_MAX_LIMIT}</span>
                  </div>
                </div>
              </div>
            </SectionWrap>

            {/* Cena */}
            <SectionWrap label="Cena (bez DPH)" open={sPrice} onToggle={() => setSPrice((v) => !v)}>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <label className="text-[10px] text-gray-400 block mb-1">Od (Kč)</label>
                  <input
                    type="number" min={PRICE_MIN} max={priceMax - 500} step={500} value={priceMin}
                    onChange={(e) => setPriceMin(Math.min(Number(e.target.value), priceMax - 500))}
                    className="w-full h-9 text-sm border border-gray-200 rounded-md px-2 focus:outline-none focus:ring-2 focus:ring-[#1a6ef5]/30 focus:border-[#1a6ef5]"
                  />
                </div>
                <span className="text-gray-300 pb-2 shrink-0">—</span>
                <div className="flex-1">
                  <label className="text-[10px] text-gray-400 block mb-1">Do (Kč)</label>
                  <input
                    type="number" min={priceMin + 500} max={PRICE_MAX} step={500} value={priceMax}
                    onChange={(e) => setPriceMax(Math.max(Number(e.target.value), priceMin + 500))}
                    className="w-full h-9 text-sm border border-gray-200 rounded-md px-2 focus:outline-none focus:ring-2 focus:ring-[#1a6ef5]/30 focus:border-[#1a6ef5]"
                  />
                </div>
              </div>
              <p className="text-[10px] text-gray-400 text-center mt-1.5">
                {formatPrice(priceMin)} – {formatPrice(priceMax)} Kč / měs.
              </p>
            </SectionWrap>

            {/* Palivo */}
            <SectionWrap label="Palivo" open={sFuel} onToggle={() => setSFuel((v) => !v)}>
              <div className="flex flex-wrap gap-1.5">
                {FUEL_OPTIONS.map((o) => (
                  <Pill
                    key={o.value}
                    label={o.label}
                    active={fuels.includes(o.value)}
                    onClick={() => toggleArr(fuels, o.value, setFuels)}
                  />
                ))}
              </div>
            </SectionWrap>

            {/* Převodovka */}
            <SectionWrap label="Převodovka" open={sTrans} onToggle={() => setSTrans((v) => !v)}>
              <div className="flex flex-wrap gap-1.5">
                {TRANSMISSION_OPTIONS.map((o) => (
                  <Pill
                    key={o.value}
                    label={o.label}
                    active={transmissions.includes(o.value)}
                    onClick={() => toggleArr(transmissions, o.value, setTransmissions)}
                  />
                ))}
              </div>
            </SectionWrap>

            {/* Výbava */}
            <SectionWrap label="Výbava a vlastnosti" open={sFeatures} onToggle={() => setSFeatures((v) => !v)}>
              <Chk label="4X4" checked={fourByFour} onChange={setFourByFour} />
              <Chk label="Navigace" checked={nav} onChange={setNav} />
              <Chk label="Tažné zařízení" checked={towBar} onChange={setTowBar} />
            </SectionWrap>

            {/* Karoserie */}
            <SectionWrap label="Karoserie" open={sBody} onToggle={() => setSBody((v) => !v)}>
              <div className="flex flex-wrap gap-1.5">
                {BODY_OPTIONS.map((o) => (
                  <Pill
                    key={o.value}
                    label={o.label}
                    active={bodies.includes(o.value)}
                    onClick={() => toggleArr(bodies, o.value, setBodies)}
                  />
                ))}
              </div>
            </SectionWrap>

            {/* Určeno pro */}
            <SectionWrap label="Určeno pro" open={sTarget} onToggle={() => setSTarget((v) => !v)}>
              <div className="flex flex-wrap gap-1.5">
                {TARGET_OPTIONS.map((o) => (
                  <Pill
                    key={o.value}
                    label={o.label}
                    active={targets.includes(o.value)}
                    onClick={() => toggleArr(targets, o.value, setTargets)}
                  />
                ))}
              </div>
            </SectionWrap>

            {/* Služby v ceně */}
            <SectionWrap label="Služby v ceně" open={sServices} onToggle={() => setSServices((v) => !v)}>
              <Chk label="Garanční prohlídky v ceně" checked={warranty} onChange={setWarranty} />
              <Chk label="Zimní a letní pneu v ceně" checked={tyres} onChange={setTyres} />
            </SectionWrap>
          </div>
        </div>
      </aside>

      {/* ── Results ── */}
      <div className="flex-1 min-w-0">
        {/* Sort bar */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-500">
            Nalezeno{" "}
            <strong className="text-gray-900">{filtered.length}</strong> nabídek
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setWithVat((v) => !v)}
              className="flex items-center gap-1 text-xs border border-gray-200 rounded-full px-3 py-1.5 hover:border-[#1a6ef5] transition-colors bg-white"
            >
              <span className={withVat ? "text-gray-400" : "font-semibold text-gray-800"}>bez DPH</span>
              <span className="text-gray-300 mx-0.5">·</span>
              <span className={withVat ? "font-semibold text-gray-800" : "text-gray-400"}>s DPH</span>
            </button>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="h-9 text-sm border border-gray-200 rounded-md px-2 focus:outline-none focus:ring-2 focus:ring-[#1a6ef5]/30 focus:border-[#1a6ef5] bg-white"
            >
              <option value="price_asc">Nejlevnější</option>
              <option value="price_desc">Nejdražší</option>
              <option value="newest">Nejnovější</option>
            </select>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-400 text-lg mb-3">Žádné nabídky neodpovídají filtru</p>
            <button
              type="button"
              onClick={resetAll}
              className="text-[#1a6ef5] text-sm hover:underline inline-flex items-center gap-1"
            >
              Zrušit filtry <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
            {filtered.map((car) => (
              <CarCard key={car.id} car={car} withVat={withVat} />
            ))}
          </div>
        )}

        {/* Mobile filter note */}
        <div className="lg:hidden mt-4 p-4 bg-blue-50 rounded-xl text-sm text-[#1a6ef5] text-center">
          Filtry jsou k dispozici na větším displeji
        </div>
      </div>
    </div>
  );
}
